<template>
  <div class="invoice-unissued-container">
    <div class="invoice-unissued-header">
      <span class="back" @click="$router.push('/profile')">&#8592;</span>
      <span class="title">{{ active === 'unissued' ? '未开发票' : '已开发票' }}</span>
    </div>
    <div class="invoice-unissued-tabs">
      <div :class="['invoice-unissued-tab', active==='unissued' && 'active']" @click="switchTab('unissued')">未开发票</div>
      <div :class="['invoice-unissued-tab', active==='issued' && 'active']" @click="switchTab('issued')">已开发票</div>
    </div>
    <div class="invoice-unissued-list" ref="listContainer">
      <div v-if="loading && display.length === 0" class="invoice-unissued-loading">加载中...</div>
      <div v-else-if="display.length === 0" class="invoice-unissued-empty">
        <div class="empty-tip-icon">📄</div>
        <div class="empty-tip-text">暂无{{ active === 'unissued' ? '未开票' : '已开票' }}账单</div>
      </div>
      <template v-else>
        <!-- 未开票页面：显示复选框和全选 -->
        <template v-if="active === 'unissued'">
          <div class="invoice-unissued-group-title">
            <span>充值记录</span>
            <span class="total-amount">共计金额：￥{{ formatAmount(selectedTotalAmount) }}</span>
          </div>
          <div class="invoice-unissued-total-card">
            <input 
              type="checkbox" 
              class="invoice-unissued-checkbox" 
              :checked="isAllSelected"
              @change="toggleSelectAll"
            />
            <span>全选</span>
          </div>
          <div class="invoice-unissued-card" v-for="(item,i) in display" :key="item.paymentId || i">
            <input 
              type="checkbox" 
              class="invoice-unissued-checkbox" 
              :checked="isSelected(item.paymentId)"
              @change="toggleSelect(item)"
            />
            <div class="invoice-unissued-main">
              <div class="invoice-unissued-time">{{ formatDate(item.payTime) }}</div>
              <div class="invoice-unissued-amount">金额：￥{{ formatAmount(item.amount) }}</div>
            </div>
          </div>
        </template>
        <!-- 已开票页面：显示已开发票和申请日期 -->
        <template v-else>
          <div class="invoice-unissued-group-title">充值记录</div>
          <div class="invoice-unissued-card invoice-issued-card" v-for="(item,i) in display" :key="item.paymentId || i">
            <div class="invoice-unissued-header-row">
              <span :class="['invoice-unissued-status', item.invoiceStatus === 'RED_INVOICE' ? 'red-invoice-status' : '']">
                {{ item.invoiceStatus === 'RED_INVOICE' ? '红冲' : '已开发票' }}
              </span>
              <span class="invoice-unissued-apply-date">申请日期：{{ formatDate(item.applyDate) }}</span>
            </div>
            <div class="invoice-unissued-main invoice-issued-main">
              <div class="invoice-unissued-amount">金额：￥{{ formatAmount(item.amount) }}</div>
              <!-- 红冲按钮已隐藏 -->
              <button 
                v-if="false"
                class="red-invoice-btn" 
                :disabled="!item.invoiceInfoId || item.invoiceStatus !== 'SUCCESS'"
                :title="getRedInvoiceDisabledReason(item)"
                @click="onRedInvoice(item)"
              >
                红冲
              </button>
            </div>
          </div>
        </template>
        <div v-if="loadingMore" class="invoice-unissued-loading-more">
          <div class="loading-spinner"></div>
          <span>加载中...</span>
        </div>
        <div v-if="noMoreData && display.length > 0 && !loadingMore" class="invoice-unissued-no-more">没有更多数据了</div>
      </template>
    </div>
    <!-- 底部去开票按钮（仅未开票页面显示） -->
    <div v-if="active === 'unissued'" class="invoice-unissued-btn-bottom">
      <button @click="goToIssue" :disabled="selectedItems.length === 0">去开票</button>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'InvoiceUnissued',
  data () {
    return {
      active: 'unissued',
      unissuedList: [],
      issuedList: [],
      loading: false,
      loadingMore: false,
      noMoreData: false,
      currentPage: 1,
      pageSize: 10,
      currentBusinessId: null,
      scrollTimer: null,
      selectedItems: [] // 选中的支付记录
    }
  },
  computed: {
    display () {
      return this.active === 'unissued' ? this.unissuedList : this.issuedList
    },
    isAllSelected () {
      if (this.active !== 'unissued' || this.display.length === 0) return false
      return this.display.every(item => this.isSelected(item.paymentId))
    },
    selectedTotalAmount () {
      return this.selectedItems.reduce((sum, item) => sum + (item.amount || 0), 0)
    }
  },
  async mounted () {
    await this.fetchBusinessId()
    if (this.currentBusinessId) {
      await this.loadInvoiceList()
    }
    // 添加滚动监听
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeUnmount () {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer)
    }
  },
  methods: {
    async fetchBusinessId () {
      try {
        // 优先从 localStorage 获取
        const savedBusiness = localStorage.getItem('currentBusiness')
        if (savedBusiness) {
          try {
            const business = JSON.parse(savedBusiness)
            this.currentBusinessId = business.businessId || business.id || null
            if (this.currentBusinessId) return
          } catch (e) {
            console.warn('解析保存的商户信息失败:', e)
          }
        }

        // 从 API 获取商户信息
        let userId = localStorage.getItem('userId')
        if (!userId) {
          const token = localStorage.getItem('token')
          if (token) {
            try {
              const parts = token.split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]))
                userId = payload.userId || payload.uid || payload.sub || null
              }
            } catch (e) {
              console.warn('从 token 解析 userId 失败:', e)
            }
          }
        }

        if (!userId) {
          console.warn('无法获取用户ID，无法查询商户信息')
          return
        }

        const merchantRes = await request({
          url: '/market/business/list',
          method: 'post',
          data: { administor: userId },
          timeout: 30000
        })

        const merchantList = merchantRes?.data || []
        if (merchantList.length > 0) {
          this.currentBusinessId = merchantList[0].businessId || merchantList[0].id || null
          // 保存到 localStorage
          if (this.currentBusinessId) {
            localStorage.setItem('currentBusiness', JSON.stringify(merchantList[0]))
          }
        }
      } catch (e) {
        console.error('获取商户ID失败:', e)
      }
    },
    async loadInvoiceList (isLoadMore = false) {
      if (!this.currentBusinessId) {
        console.warn('商户ID不存在，无法加载发票列表')
        return
      }

      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.currentPage = 1
        this.noMoreData = false
        if (this.active === 'unissued') {
          this.unissuedList = []
        } else {
          this.issuedList = []
        }
      }

      try {
        const response = await request({
          url: '/pay/businessPayment/search/page',
          method: 'post',
          data: {
            businessId: this.currentBusinessId,
            isInvoice: this.active === 'issued',
            paymentStatus: 'PAID' // 只查询已支付的记录
          },
          params: {
            page: this.currentPage,
            size: this.pageSize
          },
          timeout: 30000
        })

        const data = response?.data || []
        const list = Array.isArray(data) ? data : []

        // 格式化数据
        let formattedList = list.map(item => ({
          paymentId: item.paymentId,
          outTradeNo: item.outTradeNo,
          amount: item.amount || 0,
          payTime: item.payTime,
          payChannel: item.payChannel,
          payWay: item.payWay,
          title: this.getPaymentTitle(item),
          desc: this.getPaymentDesc(item)
        }))

        // 如果是已开票列表，需要查询发票信息获取申请时间
        if (this.active === 'issued' && formattedList.length > 0) {
          try {
            const paymentIds = formattedList.map(item => item.paymentId)
            const invoiceResponse = await request({
              url: '/invoice/invoiceInfo/search',
              method: 'post',
              data: {
                paymentIds: paymentIds,
                orderType: 'MERCHANTRE_CHARGE_FEE' // 商户充值订单类型
              },
              params: {
                pageNo: 1,
                pageSize: 1000 // 获取足够多的数据
              },
              timeout: 30000
            })

            const invoiceList = invoiceResponse?.data || []
            // 创建 paymentId -> invoiceInfo 的映射
            const invoiceMap = new Map()
            invoiceList.forEach(invoice => {
              if (invoice.paymentIds && Array.isArray(invoice.paymentIds)) {
                invoice.paymentIds.forEach(paymentId => {
                  // 如果该 paymentId 还没有映射，或者当前发票的创建时间更早（申请时间更早）
                  if (!invoiceMap.has(paymentId) || 
                      (invoice.utcCreated && invoiceMap.get(paymentId).utcCreated && 
                       new Date(invoice.utcCreated) < new Date(invoiceMap.get(paymentId).utcCreated))) {
                    invoiceMap.set(paymentId, invoice)
                  }
                })
              }
            })

            // 将发票的申请时间、发票ID和状态合并到支付记录中
            formattedList = formattedList.map(item => {
              const invoice = invoiceMap.get(item.paymentId)
              return {
                ...item,
                applyDate: invoice?.utcCreated || null, // 申请日期使用发票的创建时间
                invoiceInfoId: invoice?.id || null, // 发票信息ID，用于红冲
                invoiceStatus: invoice?.invoiceStatus || null // 发票状态，用于判断是否可以红冲
              }
            })
          } catch (e) {
            console.error('查询发票信息失败:', e)
            // 如果查询发票信息失败，仍然显示支付记录，只是没有申请日期
          }
        }

        if (this.active === 'unissued') {
          if (isLoadMore) {
            this.unissuedList = [...this.unissuedList, ...formattedList]
          } else {
            this.unissuedList = formattedList
          }
        } else {
          if (isLoadMore) {
            this.issuedList = [...this.issuedList, ...formattedList]
          } else {
            this.issuedList = formattedList
          }
        }

        // 判断是否还有更多数据
        if (formattedList.length < this.pageSize) {
          this.noMoreData = true
        } else {
          this.currentPage++
        }
      } catch (e) {
        console.error('加载发票列表失败:', e)
        const errorMsg = e?.message || e?.response?.data?.message || '加载发票列表失败'
        alert(errorMsg)
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    switchTab (tab) {
      if (this.active === tab) return
      this.active = tab
      this.currentPage = 1
      this.noMoreData = false
      this.selectedItems = [] // 切换标签时清空选择
      // 如果当前标签页没有数据，则加载
      if (tab === 'unissued' && this.unissuedList.length === 0) {
        this.loadInvoiceList()
      } else if (tab === 'issued' && this.issuedList.length === 0) {
        this.loadInvoiceList()
      }
    },
    getPaymentTitle (item) {
      const channelMap = {
        'WECHAT': '微信支付',
        'ALIPAY': '支付宝',
        'BALANCE': '余额支付'
      }
      const channel = channelMap[item.payChannel] || '支付'
      return `商户充值 - ${channel}`
    },
    getPaymentDesc (item) {
      if (item.payTime) {
        return this.formatDate(item.payTime)
      }
      return item.outTradeNo || '商户订单'
    },
    formatDate (dateStr) {
      if (!dateStr) return ''
      try {
        const date = new Date(dateStr)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      } catch (e) {
        return dateStr
      }
    },
    formatAmount (amount) {
      if (!amount && amount !== 0) return '0.00'
      // 金额单位是分，需要转换为元
      const yuan = (amount / 100).toFixed(2)
      return yuan
    },
    // 获取红冲按钮禁用原因
    getRedInvoiceDisabledReason (item) {
      if (!item.invoiceInfoId) {
        return '发票信息不存在，无法红冲'
      }
      if (item.invoiceStatus !== 'SUCCESS') {
        const statusMap = {
          'INVOICING': '发票开票中，无法红冲',
          'AUTHENTICATION_EXPIRE': '财务认证已过期，无法红冲',
          'FAILED': '发票开票失败，无法红冲',
          'RED_INVOICE': '发票已红冲，无法重复红冲'
        }
        return statusMap[item.invoiceStatus] || '只有开票成功的发票才能红冲'
      }
      return ''
    },
    // 红冲发票
    onRedInvoice (item) {
      // 检查发票状态，只有SUCCESS状态才能红冲
      if (!item.invoiceInfoId) {
        alert('发票信息不存在，无法红冲')
        return
      }
      
      if (item.invoiceStatus !== 'SUCCESS') {
        const reason = this.getRedInvoiceDisabledReason(item)
        alert(reason)
        return
      }

      // 显示确认对话框
      if (confirm('是否确定红冲发票？')) {
        this.submitRedInvoice(item.invoiceInfoId)
      }
    },
    // 提交红冲申请
    async submitRedInvoice (invoiceInfoId) {
      try {
        const response = await request({
          url: '/invoice/redInvoiceSubmit',
          method: 'post',
          data: {
            invoiceInfoId: invoiceInfoId
          },
          timeout: 30000
        })

        if (response?.code === 200) {
          alert('红冲申请已提交')
          // 刷新已开发票列表
          this.issuedList = []
          this.currentPage = 1
          this.noMoreData = false
          await this.loadInvoiceList()
        } else {
          const errorMsg = response?.message || response?.msg || '红冲申请失败'
          alert(errorMsg)
        }
      } catch (e) {
        console.error('提交红冲申请失败:', e)
        const errorMsg = e?.response?.data?.message || e?.message || '红冲申请失败，请重试'
        alert(errorMsg)
      }
    },
    toggleSelect (item) {
      const index = this.selectedItems.findIndex(i => i.paymentId === item.paymentId)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item)
      }
    },
    toggleSelectAll () {
      if (this.isAllSelected) {
        // 取消全选
        this.selectedItems = []
      } else {
        // 全选
        this.selectedItems = [...this.display]
      }
    },
    isSelected (paymentId) {
      return this.selectedItems.some(item => item.paymentId === paymentId)
    },
    goToIssue () {
      if (this.selectedItems.length === 0) {
        alert('请至少选择一条记录')
        return
      }
      // 跳转到开票页面，传递选中的支付记录信息
      const paymentIds = this.selectedItems.map(item => item.paymentId).join(',')
      const totalAmount = this.selectedTotalAmount
      // 商户端订单类型固定为 MERCHANTRE_CHARGE_FEE
      const orderType = 'MERCHANTRE_CHARGE_FEE'
      this.$router.push({
        path: '/invoice/issue',
        query: {
          paymentIds,
          totalAmount,
          orderType
        }
      })
    },
    download (item) {
      // TODO: 实现发票下载功能
      // eslint-disable-next-line no-unused-vars
      console.log('下载发票', item)
      alert('发票下载功能开发中')
    },
    handleScroll () {
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }
      this.scrollTimer = setTimeout(() => {
        if (this.loading || this.loadingMore || this.noMoreData) return

        const container = this.$refs.listContainer
        if (!container) return

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const windowHeight = window.innerHeight
        const documentHeight = document.documentElement.scrollHeight

        // 距离底部 100px 时加载更多
        if (scrollTop + windowHeight >= documentHeight - 100) {
          this.loadInvoiceList(true)
        }
      }, 200)
    }
  }
}
</script>

<style scoped>
.invoice-unissued-container {
  width: 100vw;
  max-width: 430px;
  min-height: 100vh;
  background: #f7f8fa;
  margin: 0 auto;
  box-sizing: border-box;
  padding-bottom: 80px;
}

.invoice-unissued-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
  font-size: 20px;
  font-weight: bold;
}

.invoice-unissued-header .back {
  position: absolute;
  left: 0;
  height: 56px;
  display: flex;
  align-items: center;
  font-size: 26px;
  padding-left: 16px;
  cursor: pointer;
}

.invoice-unissued-tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1.5px solid #eaeaea;
  position: sticky;
  top: 0;
  z-index: 2;
}

.invoice-unissued-tab {
  flex: 1;
  text-align: center;
  font-size: 16px;
  color: #b0b0b0;
  padding: 10px 0 8px;
  cursor: pointer;
  font-weight: 500;
  position: relative;
}

.invoice-unissued-tab.active {
  color: #3572e6;
  font-weight: 600;
}

.invoice-unissued-list {
  margin: 0;
  padding: 0;
}

.invoice-unissued-group-title {
  font-size: 16px;
  color: #222;
  font-weight: 500;
  margin: 18px 16px 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.invoice-unissued-group-title .total-amount {
  color: #ff4444;
  font-weight: bold;
  font-size: 16px;
}

.invoice-unissued-total-card {
  background: #fff;
  border-radius: 14px;
  margin: 12px 16px 12px 16px;
  padding: 14px 18px;
  box-shadow: 0 2px 8px #e0e0e033;
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #222;
  gap: 8px;
}

.invoice-unissued-card {
  background: #fff;
  border-radius: 14px;
  margin: 0 16px 12px 16px;
  padding: 16px 18px 12px 18px;
  box-shadow: 0 2px 8px #e0e0e033;
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

/* 已开票卡片使用列布局 */
.invoice-issued-card {
  flex-direction: column;
  align-items: stretch;
}

.invoice-unissued-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.invoice-unissued-status {
  font-size: 16px;
  color: #222;
  font-weight: 600;
}

.invoice-unissued-status.red-invoice-status {
  color: #ef4444;
}

.invoice-unissued-apply-date {
  font-size: 14px;
  color: #888;
}

.red-invoice-btn {
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fecaca;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 16px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  white-space: nowrap;
}

.red-invoice-btn:hover:not(:disabled) {
  background: #fee2e2;
}

.red-invoice-btn:active:not(:disabled) {
  background: #fecaca;
  transform: scale(0.98);
}

.red-invoice-btn:disabled {
  background: #f9fafb !important;
  color: #d1d5db !important;
  border-color: #e5e7eb !important;
  cursor: not-allowed;
  opacity: 0.6;
  position: relative;
}

.red-invoice-btn:disabled:hover {
  /* 保持禁用状态的样式 */
  background: #f9fafb !important;
}

.red-invoice-btn:disabled:active {
  background: #f9fafb !important;
  transform: none;
}

.invoice-unissued-checkbox {
  width: 22px;
  height: 22px;
  accent-color: #238aff;
  margin-top: 2px;
  flex-shrink: 0;
}

.invoice-unissued-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.invoice-issued-main {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.invoice-unissued-time {
  font-size: 16px;
  color: #222;
  font-weight: 500;
  margin-bottom: 4px;
}

.invoice-unissued-amount {
  font-size: 16px;
  color: #ff4444;
  font-weight: bold;
}

.total-amount {
  color: #ff4444;
}

.invoice-unissued-item {
  background: #fff;
  border-radius: 12px;
  margin: 0 16px 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px #e0e0e033;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.invoice-unissued-item-left {
  flex: 1;
  min-width: 0;
}

.invoice-unissued-item-title {
  font-size: 17px;
  font-weight: 500;
  color: #222;
  margin-bottom: 4px;
}

.invoice-unissued-item-desc {
  font-size: 14px;
  color: #888;
  margin-bottom: 4px;
}

.invoice-unissued-item-amount {
  font-size: 16px;
  font-weight: bold;
  color: #ff4444;
}

.invoice-unissued-item-right {
  margin-left: 12px;
}

.invoice-unissued-btn {
  background: #3572e6;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.invoice-unissued-btn:active {
  background: #2456b6;
  transform: scale(0.98);
}

.invoice-unissued-btn.issued-btn {
  background: #09bb07;
}

.invoice-unissued-btn.issued-btn:active {
  background: #08a006;
}

.invoice-unissued-loading,
.invoice-unissued-empty {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-tip-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-tip-text {
  font-size: 14px;
  color: #999;
}

.invoice-unissued-loading-more,
.invoice-unissued-no-more {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 14px;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3572e6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.invoice-unissued-btn-bottom {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 60px;
  max-width: 430px;
  margin: 0 auto;
  z-index: 100;
  display: flex;
  justify-content: center;
  background: transparent;
  padding: 0 16px;
}

.invoice-unissued-btn-bottom button {
  width: 90%;
  max-width: 340px;
  background: #ff4444;
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  padding: 18px 0;
  box-shadow: 0 2px 8px #ff444433;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.invoice-unissued-btn-bottom button:active {
  background: #e63333;
  transform: scale(0.98);
}

.invoice-unissued-btn-bottom button:disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
